import { format, parse } from 'date-fns';
import React, { createContext, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CREATED_AT_KEY_PARAM,
  DATE_FROM_KEY_PARAM,
  DATE_TO_KEY_PARAM,
  DESC,
  DIRECTION_KEY_PARAM,
  ENDPOINT_ORDERS,
  FILTERS_CODE,
  ORDER_KEY_PARAM,
  ORDER_STATUS,
  ORDER_STATUS_GROUPS,
  PAGE_KEY_PARAM,
  STATUS_KEY_PARAM,
  TERM_KEY_PARAM,
} from '@/common/constants';
import scrollToAnchor from '@/common/helpers/scrollToAnchor';
import useOrdersSubscription from '@/common/hooks/mercure/useOrdersSubscription';
import usePageParams from '@/common/hooks/usePageParams';
import useOrders from '@/common/queries/orders/useOrders';

import {
  OrdersContextProviderDefaultProps,
  OrdersContextProviderPropTypes,
} from './OrdersContextProvider.proptypes';

/**
 * Data parameters
 * name: url query and state name
 * defaultValue: default value on state initialization
 * encode: state to url query
 * decode : url query to state
 */
const paramsConfig = {
  [ORDER_KEY_PARAM]: {
    name: ORDER_KEY_PARAM,
    defaultValue: CREATED_AT_KEY_PARAM,
    encode: (value) => value,
    decode: (value) => value,
  },
  [DIRECTION_KEY_PARAM]: {
    name: DIRECTION_KEY_PARAM,
    defaultValue: DESC,
    encode: (value) => value,
    decode: (value) => value,
  },
  [PAGE_KEY_PARAM]: {
    name: PAGE_KEY_PARAM,
    defaultValue: 1,
    encode: (value) => value,
    decode: (value) => value,
  },
  [TERM_KEY_PARAM]: {
    name: TERM_KEY_PARAM,
    defaultValue: null,
    encode: (value) => value,
    decode: (value) => value,
  },
  [DATE_FROM_KEY_PARAM]: {
    name: DATE_FROM_KEY_PARAM,
    defaultValue: null,
    encode: (value) => (!value ? null : format(value, 'yyyy-MM-dd')),
    decode: (value) => (!value ? null : parse(value, 'yyyy-MM-dd', new Date())),
  },
  [DATE_TO_KEY_PARAM]: {
    name: DATE_TO_KEY_PARAM,
    defaultValue: null,
    encode: (value) => (!value ? null : format(value, 'yyyy-MM-dd')),
    decode: (value) => (!value ? null : parse(value, 'yyyy-MM-dd', new Date())),
  },
  [STATUS_KEY_PARAM]: {
    name: [STATUS_KEY_PARAM],
    defaultValue: [],
    encode: (value) => value,
    decode: (value) => value,
  },
};

const OrdersContext = createContext(null);

const OrdersContextProvider = ({ children, limit }) => {
  const { t } = useTranslation();

  const {
    params,
    setParams,
    getParams,
    apiParams,
    hasSearchParamsDiff,
    getQueryParams,
  } = usePageParams(paramsConfig);
  const queryKey = [ENDPOINT_ORDERS, { ...apiParams, limit }];
  const {
    data,
    isFetching,
    isLoading,
    isError,
    isSuccess,
    refetch,
  } = useOrders(queryKey, hasSearchParamsDiff);

  useOrdersSubscription(queryKey);

  /**
   * Extract status from filters
   * @return {Array}
   */
  const filtersStatus = useMemo(() => {
    let status = data?.filters.data.filter(
      (item) => item.code === FILTERS_CODE.STATUS,
    );

    if (status && status.length > 0) {
      status = status.shift();

      return status.values;
    }

    return null;
  }, [data]);

  const filtersStatusGroups = useMemo(() => {
    const statusGroups = data ? [...data.status_groups] : [];
    const invoiced = data?.filters.data
      .find((item) => item.code === FILTERS_CODE.STATUS)
      ?.values.find((item) => item.value === ORDER_STATUS.INVOICED);

    if (invoiced) {
      statusGroups.push({
        label: t('ebat:entities:orders.button.invoiced'),
        slug: ORDER_STATUS_GROUPS.INVOICED,
        data: [ORDER_STATUS.INVOICED],
        count: invoiced.count,
      });
    }

    return statusGroups;
  }, [data, t]);

  const isDisabled = useMemo(
    () => ({
      loading: isLoading || isFetching,
    }),
    [isFetching, isLoading],
  );

  const handleSort = (order, direction) =>
    setParams({
      [ORDER_KEY_PARAM]: order,
      [DIRECTION_KEY_PARAM]: direction,
      [PAGE_KEY_PARAM]: 1,
    });

  const handlePaginate = (page) => {
    scrollToAnchor('#main-body');

    setParams({
      [PAGE_KEY_PARAM]: page,
    });
  };

  const handlePeriodChange = (period) => {
    setParams({
      [DATE_FROM_KEY_PARAM]: period[0],
      [DATE_TO_KEY_PARAM]: period[1],
      [PAGE_KEY_PARAM]: 1,
    });
  };

  const handleFilter = (status) =>
    setParams({
      [STATUS_KEY_PARAM]: status,
      [PAGE_KEY_PARAM]: 1,
    });

  const handleSearch = (term) =>
    setParams({
      [TERM_KEY_PARAM]: term !== '' ? term : null,
      [PAGE_KEY_PARAM]: 1,
    });

  return (
    <OrdersContext.Provider
      value={{
        data,
        filtersStatus,
        filtersStatusGroups,
        getParams,
        getQueryParams,
        handleFilter,
        handlePaginate,
        handlePeriodChange,
        handleSearch,
        handleSort,
        isDisabled,
        isError,
        isSuccess,
        params,
        refetch,
        setParams,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
};

OrdersContextProvider.defaultProps = {
  ...OrdersContextProviderDefaultProps,
};

OrdersContextProvider.propTypes = {
  ...OrdersContextProviderPropTypes,
};

export const useOrdersContext = () => useContext(OrdersContext);

export default OrdersContextProvider;
